import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShoppingCart, User, Search, Menu, X } from 'lucide-react';

const Header23 = ({ cartCount = 0 }) => {
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        if (mobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => {
            document.body.style.overflow = '';
        };
    }, [mobileMenuOpen]);

    const navLinks = [
        { label: 'Home', path: '/template23' },
        { label: 'Wedding Shop', path: '/template23/shop' },
        { label: 'Festive Edit', path: '/template23/collections' },
        { label: 'New Arrivals', path: '/template23/new-arrivals' },
        { label: 'About', path: '/template23/about' },
        { label: 'Orders', path: '/template23/orders' },
    ];

    const handleNavClick = (path) => {
        navigate(path);
        setMobileMenuOpen(false);
    };

    return (
        <>
            <style>{`
                @import url('https://fonts.googleapis.com/css2?family=Libre+Baskerville:ital,wght@0,400;0,700;1,400&family=Nunito+Sans:wght@300;400;500;600;700&display=swap');
            `}</style>

            <header
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    zIndex: 50,
                    backgroundColor: scrolled ? '#FFFFFF' : 'rgba(255, 253, 248, 0.95)',
                    borderBottom: scrolled ? '1px solid #E8E0D4' : '1px solid transparent',
                    transition: 'all 0.3s ease',
                    backdropFilter: 'blur(8px)',
                    boxShadow: scrolled ? '0 2px 12px rgba(0,0,0,0.06)' : 'none',
                }}
            >
                {/* Top Announcement Bar */}
                <div
                    style={{
                        backgroundColor: '#1B3A2D',
                        textAlign: 'center',
                        padding: '8px 16px',
                    }}
                >
                    <p
                        style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '12px',
                            fontWeight: 600,
                            color: '#C5974B',
                            margin: 0,
                            letterSpacing: '1.5px',
                            textTransform: 'uppercase',
                        }}
                    >
                        Free Shipping on Orders Above &#8377;2,999 | Wedding Season Sale Live
                    </p>
                </div>

                <div
                    style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 24px',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        height: '72px',
                    }}
                >
                    {/* Left: Brand */}
                    <h1
                        onClick={() => navigate('/template23')}
                        style={{
                            fontFamily: "'Libre Baskerville', Georgia, serif",
                            fontSize: '26px',
                            fontWeight: 700,
                            color: '#1B3A2D',
                            cursor: 'pointer',
                            letterSpacing: '3px',
                            margin: 0,
                        }}
                    >
                        UTSAV
                    </h1>

                    {/* Center: Nav Links (Desktop) */}
                    <nav
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '32px',
                        }}
                        className="utsav-desktop-nav"
                    >
                        {navLinks.map((link) => (
                            <button
                                key={link.label}
                                onClick={() => handleNavClick(link.path)}
                                style={{
                                    fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                    fontSize: '13px',
                                    fontWeight: 600,
                                    color: '#1B3A2D',
                                    background: 'none',
                                    border: 'none',
                                    cursor: 'pointer',
                                    padding: 0,
                                    letterSpacing: '1px',
                                    textTransform: 'uppercase',
                                    transition: 'color 0.3s ease',
                                    position: 'relative',
                                }}
                                onMouseEnter={(e) => (e.target.style.color = '#C5974B')}
                                onMouseLeave={(e) => (e.target.style.color = '#1B3A2D')}
                            >
                                {link.label}
                            </button>
                        ))}
                    </nav>

                    {/* Right: Icons */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '18px' }}>
                        <Search
                            size={20}
                            style={{ color: '#1B3A2D', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#C5974B')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1B3A2D')}
                        />
                        <User
                            size={20}
                            style={{ color: '#1B3A2D', cursor: 'pointer', transition: 'color 0.3s ease' }}
                            onClick={() => navigate('/template23/profile')}
                            onMouseEnter={(e) => (e.currentTarget.style.color = '#C5974B')}
                            onMouseLeave={(e) => (e.currentTarget.style.color = '#1B3A2D')}
                        />
                        <div
                            style={{ position: 'relative', cursor: 'pointer' }}
                            onClick={() => navigate('/template23/cart')}
                        >
                            <ShoppingCart
                                size={20}
                                style={{ color: '#1B3A2D', transition: 'color 0.3s ease' }}
                                onMouseEnter={(e) => (e.currentTarget.style.color = '#C5974B')}
                                onMouseLeave={(e) => (e.currentTarget.style.color = '#1B3A2D')}
                            />
                            {cartCount > 0 && (
                                <span
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        backgroundColor: '#C5974B',
                                        color: '#FFFFFF',
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                                        width: '18px',
                                        height: '18px',
                                        borderRadius: '50%',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }}
                                >
                                    {cartCount}
                                </span>
                            )}
                        </div>

                        {/* Mobile Hamburger */}
                        <button
                            className="utsav-mobile-menu-btn"
                            onClick={() => setMobileMenuOpen(true)}
                            style={{
                                display: 'none',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                            }}
                        >
                            <Menu size={24} style={{ color: '#1B3A2D' }} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile Full-Screen Overlay */}
            <div
                style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: 100,
                    backgroundColor: '#FFFDF8',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    opacity: mobileMenuOpen ? 1 : 0,
                    pointerEvents: mobileMenuOpen ? 'auto' : 'none',
                    transition: 'opacity 0.3s ease',
                }}
            >
                {/* Close Button */}
                <button
                    onClick={() => setMobileMenuOpen(false)}
                    style={{
                        position: 'absolute',
                        top: '24px',
                        right: '24px',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        padding: 0,
                    }}
                >
                    <X size={28} style={{ color: '#1B3A2D' }} />
                </button>

                {/* Brand Name in Mobile Menu */}
                <h2
                    style={{
                        fontFamily: "'Libre Baskerville', Georgia, serif",
                        fontSize: '32px',
                        fontWeight: 700,
                        color: '#C5974B',
                        letterSpacing: '4px',
                        marginBottom: '48px',
                    }}
                >
                    UTSAV
                </h2>

                <nav style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '28px' }}>
                    {navLinks.map((link) => (
                        <button
                            key={link.label}
                            onClick={() => handleNavClick(link.path)}
                            style={{
                                fontFamily: "'Libre Baskerville', Georgia, serif",
                                fontSize: '20px',
                                fontWeight: 400,
                                color: '#1B3A2D',
                                background: 'none',
                                border: 'none',
                                cursor: 'pointer',
                                padding: 0,
                                letterSpacing: '2px',
                                textTransform: 'uppercase',
                                transition: 'color 0.3s ease',
                            }}
                            onMouseEnter={(e) => (e.target.style.color = '#C5974B')}
                            onMouseLeave={(e) => (e.target.style.color = '#1B3A2D')}
                        >
                            {link.label}
                        </button>
                    ))}
                </nav>

                {/* Mobile Menu Footer */}
                <div style={{ marginTop: '48px', textAlign: 'center' }}>
                    <p
                        style={{
                            fontFamily: "'Nunito Sans', -apple-system, sans-serif",
                            fontSize: '13px',
                            color: '#C5974B',
                            letterSpacing: '1px',
                        }}
                    >
                        Celebrating Indian Fashion
                    </p>
                </div>
            </div>

            {/* Responsive Styles */}
            <style>{`
                .utsav-desktop-nav {
                    display: flex !important;
                }
                .utsav-mobile-menu-btn {
                    display: none !important;
                }
                @media (max-width: 768px) {
                    .utsav-desktop-nav {
                        display: none !important;
                    }
                    .utsav-mobile-menu-btn {
                        display: block !important;
                    }
                }
            `}</style>
        </>
    );
};

export default Header23;
